import React, { useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import Modal from "./Modal";
import "./style.css";
import { formatInr, getResolvedMealPrices, hasPrice } from "./foodPrices";

const DEFAULT_MENU_VEG = { src: "/DefaultMenu-Veg.png", download: "/DefaultMenu-Veg.png" };
const DEFAULT_MENU_VEG_NONVEG = {
  src: "/DefaultMenu-VegNonVeg.png",
  download: "/DefaultMenu-VegNonVeg.png",
};

/** Placeholder menus — only when backend has zero meal_media */
const getDefaultMenuImages = (offersVeg, offersNonVeg) => {
  if (offersVeg && offersNonVeg) {
    return [DEFAULT_MENU_VEG, DEFAULT_MENU_VEG_NONVEG];
  }
  if (offersNonVeg) {
    return [DEFAULT_MENU_VEG_NONVEG];
  }
  return [DEFAULT_MENU_VEG];
};

export const getMealTypes = (villaData) => {
  const hasVegPrice =
    hasPrice(villaData?.meal_package_veg_adult_price) ||
    hasPrice(villaData?.meal_package_veg_child_price);
  const hasNonVegPrice =
    hasPrice(villaData?.meal_package_non_veg_adult_price) ||
    hasPrice(villaData?.meal_package_non_veg_child_price);

  if (villaData?.only_veg_meal === true) {
    return { offersVeg: true, offersNonVeg: false };
  }

  if (villaData?.only_veg_meal === false) {
    return { offersVeg: true, offersNonVeg: true };
  }

  if (hasVegPrice || hasNonVegPrice) {
    return {
      offersVeg: hasVegPrice,
      offersNonVeg: hasNonVegPrice,
    };
  }

  if (villaData?.food_package) {
    return { offersVeg: true, offersNonVeg: false };
  }

  return { offersVeg: false, offersNonVeg: false };
};

const MealTypeLabel = ({ offersVeg, offersNonVeg }) => {
  if (offersVeg && offersNonVeg) {
    return (
      <>
        <span className="text-green-500 font-semibold">Veg</span>/
        <span className="text-red-600 font-semibold">Non Veg</span>
      </>
    );
  }
  if (offersVeg) {
    return <span className="text-green-500 font-semibold">Veg</span>;
  }
  if (offersNonVeg) {
    return <span className="text-red-600 font-semibold">Non Veg</span>;
  }
  return null;
};

const downloadMenuImage = async (url, index) => {
  const filename = `food-menu-${index + 1}.jpg`;
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

/** Pull meal menu URLs from API — never mix with A2Z defaults */
const getBackendMenuImages = (villaData) => {
  const mealMedia = Array.isArray(villaData?.meal_media)
    ? villaData.meal_media
    : [];
  return mealMedia
    .map((item) => {
      const src =
        item?.file ||
        item?.media?.file ||
        (typeof item === "string" ? item : null);
      if (!src || typeof src !== "string") return null;
      const trimmed = src.trim();
      if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
      // Never treat local A2Z placeholder paths as "backend" uploads
      if (
        trimmed.startsWith("/DefaultMenu") ||
        trimmed.includes("DefaultMenu-Veg") ||
        trimmed.includes("DefaultMenu-VegNonVeg")
      ) {
        return null;
      }
      return { src: trimmed, download: trimmed };
    })
    .filter(Boolean);
};

const MenuCards = ({ images, openAt }) => {
  if (!images.length) return null;
  return (
    <div
      className={`grid gap-3 w-full mx-auto ${
        images.length === 1
          ? "grid-cols-1 max-w-[220px]"
          : images.length === 2
            ? "grid-cols-2 max-w-[420px]"
            : "grid-cols-2 max-w-[440px]"
      }`}
    >
      {images.map((img, index) => (
        <button
          key={`${img.src}-${index}`}
          type="button"
          onClick={() => openAt(index)}
          className="group relative w-full h-[220px] sm:h-[260px] rounded-2xl overflow-hidden border border-[#E5E5E5] bg-[#F5F5F5] shadow-md hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-black/20"
          aria-label={`Zoom food menu ${index + 1}`}
          title="Click to zoom"
        >
          <img
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
            src={img.src}
            alt={`Food menu ${index + 1}`}
          />
          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </button>
      ))}
    </div>
  );
};

const FoodOptions = ({ villaData }) => {
  const { offersVeg, offersNonVeg } = useMemo(
    () => getMealTypes(villaData),
    [villaData]
  );
  const mealPrices = useMemo(
    () => getResolvedMealPrices(villaData),
    [villaData]
  );
  const chefCharge = Number(villaData?.chef_charged);
  const showChef =
    villaData?.chef_charged != null && villaData?.chef_charged !== -1;
  const showFoodPackage =
    (villaData?.food_package === true || offersVeg || offersNonVeg) &&
    (offersVeg || offersNonVeg);
  const showNearby =
    villaData?.nearby_restaurant === true ||
    villaData?.nearby_restaurant == null;

  const images = useMemo(() => {
    const fromApi = getBackendMenuImages(villaData);
    // If property already has uploaded menus, show ONLY those
    if (fromApi.length > 0) return fromApi;
    return getDefaultMenuImages(offersVeg, offersNonVeg);
  }, [villaData, offersVeg, offersNonVeg]);

  const [open, setOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const openAt = (index) => {
    setLightboxIndex(index);
    setOpen(true);
  };

  return (
    <div className="font-jt my-8 min-h-[320px]">
      <div className="text-[28px] lg:text-[50px] text-center font-bold my-4 lg:m-8 font-jt">
        Food Options
      </div>

      {/* Mobile: menus first so Food tab lands on cards, not Nearby */}
      <div className="lg:hidden w-full px-4 mb-4">
        <MenuCards images={images} openAt={openAt} />
      </div>

      <div className="flex flex-col lg:flex-row py-4 lg:mx-auto text-[16px] text-black">
        <div className="w-[95%] lg:w-[65%] flex flex-col items-center justify-center mx-auto lg:mx-0">
          <ul className="lg:text-[24px] text-[19px] w-full lg:w-auto lg:font-semibold list-outside">
            {villaData?.self_cook ? (
              <li className="list-disc ml-8 lg:ml-0 my-5">
                The guest can cook by Himself
              </li>
            ) : null}
            {showChef ? (
              <li className="list-disc ml-8 lg:ml-0 my-5">
                {chefCharge > 0 ? (
                  <>
                    The guest can hire a cook from the property at{" "}
                    {formatInr(chefCharge)} per day. Groceries charges are extra
                  </>
                ) : (
                  <>
                    The guest can hire a cook from the property (complimentary).
                    Groceries charges are extra
                  </>
                )}
              </li>
            ) : null}
            {showFoodPackage ? (
              <li className="list-disc ml-8 lg:ml-0 my-5">
                The guest can select a{" "}
                <MealTypeLabel
                  offersVeg={offersVeg}
                  offersNonVeg={offersNonVeg}
                />{" "}
                food package
                <ul className="mt-2 ml-1 text-[16px] lg:text-[20px] font-medium space-y-1">
                  {offersVeg ? (
                    <li>
                      Veg — Adult {formatInr(mealPrices.vegAdult)} · Child{" "}
                      {formatInr(mealPrices.vegChild)}
                    </li>
                  ) : null}
                  {offersNonVeg ? (
                    <li>
                      Non Veg — Adult {formatInr(mealPrices.nonVegAdult)} · Child{" "}
                      {formatInr(mealPrices.nonVegChild)}
                    </li>
                  ) : null}
                </ul>
              </li>
            ) : null}
            {showNearby ? (
              <li className="list-disc ml-8 lg:ml-0 my-5">
                The guest can order from nearby restaurants or can Zomato,
                Swiggy.{" "}
              </li>
            ) : null}
          </ul>
          <div className="flex flex-row justify-center lg:justify-start space-x-4 mt-4 w-full lg:w-auto px-4 lg:px-0">
            {images.length > 0 ? (
              <button
                type="button"
                className="px-4 p-2 shadow-lg border-black border-2 rounded-full"
                onClick={() => openAt(0)}
              >
                See Food Menu
              </button>
            ) : null}
            <button
              type="button"
              className="px-6 p-2 shadow-lg lg:bg-white bg-black lg:text-black text-white border-black border-2 rounded-full"
              onClick={() => setIsOpen(true)}
            >
              View More
            </button>
          </div>
        </div>

        <div className="hidden lg:block w-full lg:w-[40%] mt-6 lg:mt-0 px-4">
          <MenuCards images={images} openAt={openAt} />
        </div>

        <Lightbox
          open={open}
          close={() => setOpen(false)}
          index={lightboxIndex}
          slides={images}
          plugins={[Thumbnails, Zoom]}
          zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
          render={{
            buttonPrev: images.length <= 1 ? () => null : undefined,
            buttonNext: images.length <= 1 ? () => null : undefined,
            controls: () => (
              <button
                type="button"
                onClick={() => {
                  const current = images[lightboxIndex] || images[0];
                  if (current) {
                    downloadMenuImage(
                      current.download || current.src,
                      lightboxIndex
                    );
                  }
                }}
                className="yarl__button"
                style={{
                  position: "absolute",
                  bottom: 16,
                  right: 16,
                  zIndex: 1,
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  borderRadius: 999,
                  padding: "8px 14px",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Download
              </button>
            ),
          }}
          on={{
            view: ({ index }) => setLightboxIndex(index),
          }}
        />
      </div>
      <Modal
        villaData={villaData}
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        offersVeg={offersVeg}
        offersNonVeg={offersNonVeg}
        mealPrices={mealPrices}
      />
    </div>
  );
};

export default FoodOptions;
